import { lazy } from 'react';

import { Navigate, Outlet, Route } from 'react-router';

import { PERMISSIONS, PermissionGate } from 'modules/permission';
import MainLayout from 'routes/components/layout';
import Loadable from 'routes/components/Loadable';
import AuthGuard from 'routes/components/route-guard/AuthGuard';
import { withOutletForModal } from 'utils/withOutletForModal';

// pages routing
const OrderDetailPage = Loadable(lazy(() => import('pages/orders/detail')));
const OrdersAnalyticsPage = Loadable(lazy(() => import('pages/orders/analytics')));
const OrdersHistoryPage = Loadable(lazy(() => import('pages/orders/history')));
const OrdersTodayPage = Loadable(lazy(() => import('pages/orders/today')));
const OrdersReviewsPage = Loadable(lazy(() => import('pages/orders/reviews')));
const OrdersStatusBoard = Loadable(lazy(() => import('pages/orders/statusBoard')));
const OrderAddressPage = Loadable(lazy(() => import('pages/orders/editAddress')));
const DeliveriesHistoryPage = Loadable(lazy(() => import('pages/fleet/deliveries/history')));
const DeliveriesActivePage = Loadable(lazy(() => import('pages/fleet/deliveries/active')));
const DeliveriesPendingPage = Loadable(lazy(() => import('pages/fleet/deliveries/pending')));
const DeliveriesDetailsModal = Loadable(lazy(() => import('pages/fleet/deliveries/detail')));
const DriversPage = Loadable(lazy(() => import('pages/fleet/drivers')));
const DriverDetailsPage = Loadable(lazy(() => import('pages/fleet/drivers/details')));
const PerformanceReportPage = Loadable(lazy(() => import('pages/fleet/performance-report')));
const AreasPage = Loadable(lazy(() => import('pages/warehouses/areas')));
const BulkUpdateAreasPage = Loadable(lazy(() => import('pages/warehouses/areas/BulkUpdateAreas')));
const ProductsPage = Loadable(lazy(() => import('pages/contents/products')));
const ProductDetailPage = Loadable(lazy(() => import('pages/contents/product-detail')));
const UploadPartnerProductsPage = Loadable(lazy(() => import('pages/contents/products/UploadPartnerProducts')));
const UploadArabicTranslationsPage = Loadable(lazy(() => import('pages/contents/products/UploadArabicTranslations')));
const PartnerTrackingListPage = Loadable(lazy(() => import('pages/contents/partner-trackings')));
const PickersPage = Loadable(lazy(() => import('pages/warehouses/pickers')));
const CustomersPage = Loadable(lazy(() => import('pages/customers')));
const CustomerDetailsPage = Loadable(lazy(() => import('pages/customers/details')));
const CustomerAddressEditModal = Loadable(lazy(() => import('pages/customers/addresses/edit')));
const BannersPage = Loadable(lazy(() => import('pages/marketing/banners')));
const CategoriesV2Page = Loadable(lazy(() => import('pages/contents/categories')));
const CategoryEditV2Page = Loadable(lazy(() => import('pages/contents/category-edit')));
const CategoriesSyncPage = Loadable(lazy(() => import('pages/contents/categories-sync')));
const WarehousesPage = Loadable(lazy(() => import('pages/warehouses/warehouses')));
const DiscountsPage = Loadable(lazy(() => import('pages/marketing/discounts')));
const AddEditDiscountPage = Loadable(lazy(() => import('pages/marketing/discounts/AddEditDiscount')));
const CompensationsPage = Loadable(lazy(() => import('pages/marketing/compensations')));
const CollectionsPage = Loadable(lazy(() => import('pages/contents/collections')));
const EmployeesPage = Loadable(lazy(() => import('pages/admin/employees')));
const SyncPage = Loadable(lazy(() => import('pages/settings/sync')));
const BatchingPage = Loadable(lazy(() => import('pages/fleet/deliveries/batching')));
const DeliverySettingsPage = Loadable(lazy(() => import('pages/settings/delivery')));
const AreasSettingsPage = Loadable(lazy(() => import('pages/settings/areas')));
const LargeOrderEtaSettingsPage = Loadable(lazy(() => import('pages/settings/largeOrderEta')));
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const WarehouseLabelPage = Loadable(lazy(() => import('pages/tools/operations/warehouseLabel')));
const TalabatCataloguePage = Loadable(lazy(() => import('pages/tools/operations/talabatCatalogue')));
const PaymentSettings = Loadable(lazy(() => import('pages/settings/payments')));
const DiscountsSettingsPage = Loadable(lazy(() => import('pages/settings/discounts')));
const DeliverooSettingsPage = Loadable(lazy(() => import('pages/settings/deliveroo')));
const CashReportPage = Loadable(lazy(() => import('pages/reports/cashReport')));
const ProductInsightsPage = Loadable(lazy(() => import('pages/reports/productInsights')));
const DeliveryChallengeAnalyticsPage = Loadable(lazy(() => import('pages/reports/delivery-challenge-analytics')));
const AppUsagePage = Loadable(lazy(() => import('pages/reports/app-usage')));
const PerformanceTrendPage = Loadable(lazy(() => import('pages/fleet/performance-trend')));
const LoyaltyPage = Loadable(lazy(() => import('pages/marketing/loyalty')));
const AddEditLoyaltyProgramPage = Loadable(lazy(() => import('pages/marketing/loyalty/AddEditProgram')));
const SubCategoriesPage = Loadable(lazy(() => import('pages/contents/subcategories')));
const SubCategoryEditPage = Loadable(lazy(() => import('pages/contents/subcategory-edit')));
const FeaturedCategoriesPage = Loadable(lazy(() => import('pages/contents/featured-categories')));
const AddEditFeaturedCategoryPage = Loadable(lazy(() => import('pages/contents/featured-categories/AddEditFeaturedCategory')));
const SortFeaturedCategoriesPage = Loadable(lazy(() => import('pages/contents/featured-categories/SortFeaturedCategories')));
const CustomerSegmentsPage = Loadable(lazy(() => import('pages/marketing/customerSegments')));
const AddCustomerSegmentPage = Loadable(lazy(() => import('pages/marketing/customerSegments/AddSegment')));
const EditCustomerSegmentPage = Loadable(lazy(() => import('pages/marketing/customerSegments/EditSegment/EditSegmentPage')));
const ViewCustomerSegmentPage = Loadable(lazy(() => import('pages/marketing/customerSegments/ViewSegment')));
const UploadCustomerSegmentPage = Loadable(lazy(() => import('pages/marketing/customerSegments/UploadSegment')));
const DeliveryChallengesPage = Loadable(lazy(() => import('pages/marketing/delivery-challenges')));
const AddEditDeliveryChallengePage = Loadable(lazy(() => import('pages/marketing/delivery-challenges/AddEditDeliveryChallenge')));
const NotificationsPage = Loadable(lazy(() => import('pages/marketing/notifications')));
const AddEditNotificationPage = Loadable(lazy(() => import('pages/marketing/notifications/AddEditNotification')));
const NotificationLogsPage = Loadable(lazy(() => import('pages/marketing/notifications/NotificationLogs')));
const PersonalizedFeaturedCategoriesProductUploadPage = Loadable(
  lazy(() => import('pages/contents/featured-categories/PersonalizedProductUpload'))
);
const SpotlightPage = Loadable(lazy(() => import('pages/contents/spotlight')));
const SpotlightEditPage = Loadable(lazy(() => import('pages/contents/spotlight-edit')));

const MainRoutes = (
  <Route path="/" element={<AuthGuard children={<Outlet />} />}>
    <Route path="/" element={<MainLayout />}>
      <Route index element={<Navigate to="orders/today" />} />

      <Route path="orders" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.ORDERS.READ} />}>
        <Route index element={<Navigate to="today" />} />
        <Route path="today" element={withOutletForModal(OrdersTodayPage)}>
          <Route path="order/:orderNumber" element={<OrderDetailPage />} />
        </Route>
        <Route path="history" element={withOutletForModal(OrdersHistoryPage)}>
          <Route path="order/:orderNumber" element={<OrderDetailPage />} />
        </Route>
        <Route path="status-board" element={withOutletForModal(OrdersStatusBoard)}>
          <Route path="order/:orderNumber" element={<OrderDetailPage />} />
        </Route>
        <Route path="edit-address" element={<OrderAddressPage />} />
      </Route>

      <Route path="customers" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.CUSTOMERS.READ} />}>
        <Route index element={<Navigate to="list" />} />
        <Route path="list">
          <Route index element={<CustomersPage />} />
          <Route path=":id" element={withOutletForModal(CustomerDetailsPage)}>
            <Route path="order/:orderNumber" element={<OrderDetailPage />} />
            <Route path="addresses/:addressId">
              <Route index element={<Navigate to="edit" />} />
              <Route path="edit" element={<CustomerAddressEditModal />} />
            </Route>
          </Route>
        </Route>
        <Route path="reviews" element={withOutletForModal(OrdersReviewsPage)}>
          <Route path="order/:orderNumber" element={<OrderDetailPage />} />
        </Route>
        <Route path="customerSegments" element={<PermissionGate children={<Outlet />} permission={PERMISSIONS.SEGMENTS.READ} />}>
          <Route index element={<CustomerSegmentsPage />} />
          <Route path="create" element={<AddCustomerSegmentPage />} />
          <Route path="upload" element={<UploadCustomerSegmentPage />} />
          <Route path=":id/edit" element={<EditCustomerSegmentPage />} />
          <Route path=":id" element={<ViewCustomerSegmentPage />} />
        </Route>
      </Route>

      <Route path="fleet">
        <Route index element={<Navigate to="deliveries/active" />} />
        <Route path="deliveries" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.DELIVERIES.READ} />}>
          <Route index element={<Navigate to="today" />} />
          <Route path="active" element={withOutletForModal(DeliveriesActivePage)}>
            <Route path=":id" element={<DeliveriesDetailsModal />} />
            <Route path="order/:orderNumber" element={<OrderDetailPage />} />
          </Route>

          <Route path="history" element={withOutletForModal(DeliveriesHistoryPage)}>
            <Route path=":id" element={<DeliveriesDetailsModal />} />
            <Route path="order/:orderNumber" element={<OrderDetailPage />} />
          </Route>

          <Route path="batching" element={<BatchingPage />} />

          <Route path="pending" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.DELIVERIES.COMPLETE} />}>
            <Route index element={<DeliveriesPendingPage />} />
            <Route path=":id" element={<DeliveriesDetailsModal />} />
          </Route>
        </Route>

        <Route path="drivers" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.DRIVERS.READ} />}>
          <Route index element={<DriversPage />} />
          <Route path=":id" element={withOutletForModal(DriverDetailsPage)}>
            <Route path="order/:orderNumber" element={<OrderDetailPage />} />
          </Route>
        </Route>

        <Route
          path="performance-report"
          element={<PermissionGate showError children={<PerformanceReportPage />} permission={PERMISSIONS.DELIVERIES.READ} />}
        />
      </Route>

      <Route path="marketing">
        <Route index element={<Navigate to="discounts" />} />
        <Route path="banners" element={<PermissionGate showError children={<BannersPage />} permission={PERMISSIONS.BANNERS.READ} />} />
        <Route path="discounts" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.DISCOUNTS.READ} />}>
          <Route index element={<DiscountsPage />} />
          <Route path="create" element={<AddEditDiscountPage />} />
          <Route path=":id" element={<AddEditDiscountPage />} />
        </Route>
        <Route
          path="compensations"
          element={<PermissionGate showError children={withOutletForModal(CompensationsPage)} permission={PERMISSIONS.DISCOUNTS.READ} />}
        >
          <Route path="order/:orderNumber" element={<OrderDetailPage />} />
        </Route>
        <Route path="loyalty" element={<PermissionGate children={<Outlet />} permission={PERMISSIONS.DISCOUNTS.READ} />}>
          <Route index element={<LoyaltyPage />} />
          <Route path="create" element={<AddEditLoyaltyProgramPage />} />
          <Route path=":id" element={<AddEditLoyaltyProgramPage />} />
        </Route>
        <Route path="notifications" element={<PermissionGate children={<Outlet />} permission={PERMISSIONS.NOTIFICATIONS.READ} />}>
          <Route index element={<NotificationsPage />} />
          <Route path="create" element={<AddEditNotificationPage />} />
          <Route path=":id" element={<AddEditNotificationPage />} />
          <Route path="logs/:id" element={withOutletForModal(NotificationLogsPage)}>
            <Route path="order/:orderNumber" element={<OrderDetailPage />} />
          </Route>
        </Route>
        <Route
          path="delivery-challenges"
          element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.DELIVERY_CHALLENGES.READ} />}
        >
          <Route index element={<DeliveryChallengesPage />} />
          <Route path="create" element={<AddEditDeliveryChallengePage />} />
          <Route path=":deliveryChallengeId" element={<AddEditDeliveryChallengePage />} />
        </Route>
      </Route>

      <Route path="contents">
        <Route index element={<Navigate to="categories" />} />
        <Route path="categories" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.CATEGORIES.READ} />}>
          <Route index element={<CategoriesV2Page />} />
          <Route path=":categoryId" element={<CategoryEditV2Page />} />
        </Route>

        <Route path="collections" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.COLLECTIONS.READ} />}>
          <Route index element={<CollectionsPage />} />
        </Route>

        <Route
          path="featured-categories"
          element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.CATEGORIES.READ} />}
        >
          <Route index element={<FeaturedCategoriesPage />} />
          <Route path="create" element={<AddEditFeaturedCategoryPage />} />
          <Route path=":featuredCategoryId" element={<AddEditFeaturedCategoryPage />} />
          <Route path="personalized" element={<PersonalizedFeaturedCategoriesProductUploadPage />} />
          <Route path="sort-widgets" element={<SortFeaturedCategoriesPage />} />
        </Route>

        <Route path="products" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.PRODUCTS.READ} />}>
          <Route index element={<ProductsPage />} />
          <Route path=":productId" element={<ProductDetailPage />} />
          <Route path="partner-products-upload" element={<UploadPartnerProductsPage />} />
          <Route path="arabic-translations-upload" element={<UploadArabicTranslationsPage />} />
        </Route>

        <Route path="partner-trackings" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.PRODUCTS.READ} />}>
          <Route index element={<PartnerTrackingListPage />} />
        </Route>

        <Route path="subcategories" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.CATEGORIES.READ} />}>
          <Route index element={<SubCategoriesPage />} />
          <Route path=":subcategoryId/:collectionName?" element={<SubCategoryEditPage />} />
        </Route>
        <Route
          path="categories-sync"
          element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.CATEGORIES.UPDATE} />}
        >
          <Route index element={<CategoriesSyncPage />} />
        </Route>

        <Route
          path="spotlight-items"
          element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.SPOTLIGHT_ITEMS.READ} />}
        >
          <Route index element={<SpotlightPage />} />
          <Route path="create" element={<SpotlightEditPage />} />
          <Route path=":subcategoryId" element={<SpotlightEditPage />} />
        </Route>
      </Route>

      <Route path="warehouses">
        <Route index element={<WarehousesPage />} />
        <Route path="areas" element={<PermissionGate showError children={<Outlet />} permission={PERMISSIONS.AREAS.READ} />}>
          <Route index element={<AreasPage />} />
          <Route
            path="bulk-update"
            element={<PermissionGate showError children={<BulkUpdateAreasPage />} permission={PERMISSIONS.AREAS.UPDATE} />}
          />
        </Route>
        <Route path="pickers" element={<PermissionGate showError children={<PickersPage />} permission={PERMISSIONS.PICKERS.READ} />} />
      </Route>

      <Route path="tools">
        <Route path="operations">
          <Route index element={<Navigate to="warehouseLabel" />} />
          <Route path="warehouseLabel" element={<WarehouseLabelPage />} />
          <Route
            path="talabatCatalogue"
            element={<PermissionGate showError children={<TalabatCataloguePage />} permission={PERMISSIONS.PRODUCTS.READ} />}
          />
        </Route>
      </Route>

      <Route path="admin">
        <Route index element={<Navigate to="employees" />} />
        <Route
          path="employees"
          element={<PermissionGate showError children={<EmployeesPage />} permission={PERMISSIONS.EMPLOYEES.READ} />}
        />
        <Route path="settings">
          <Route index element={<Navigate to="batching" />} />
          <Route path="shopify-sync" element={<PermissionGate showError children={<SyncPage />} permission={PERMISSIONS.SHOPIFY.SYNC} />} />
          <Route
            path="delivery"
            element={<PermissionGate showError children={<DeliverySettingsPage />} permission={PERMISSIONS.DELIVERY_SETTINGS.MANAGE} />}
          />
          <Route
            path="payments"
            element={<PermissionGate showError children={<PaymentSettings />} permission={PERMISSIONS.PAYMENTS_SETTINGS.MANAGE} />}
          />
          <Route
            path="largeOrderEta"
            element={
              <PermissionGate showError children={<LargeOrderEtaSettingsPage />} permission={PERMISSIONS.LARGE_ORDER_ETA_SETTINGS.MANAGE} />
            }
          />
          <Route
            path="deliveroo"
            element={<PermissionGate showError children={<DeliverooSettingsPage />} permission={PERMISSIONS.WAREHOUSES.MANAGE} />}
          />
          <Route
            path="discountsSettings"
            element={<PermissionGate showError children={<DiscountsSettingsPage />} permission={PERMISSIONS.DISCOUNTS_SETTINGS.MANAGE} />}
          />
          <Route
            path="areasSettings"
            element={<PermissionGate showError children={<AreasSettingsPage />} permission={PERMISSIONS.AREAS_SETTINGS.MANAGE} />}
          />
        </Route>
      </Route>

      <Route path="reports">
        <Route index element={<Navigate to="order-analytics" />} />
        <Route
          path="order-analytics"
          element={<PermissionGate showError children={<OrdersAnalyticsPage />} permission={PERMISSIONS.REPORTS.READ} />}
        />
        <Route
          path="performance-report"
          element={<PermissionGate showError children={<PerformanceReportPage />} permission={PERMISSIONS.REPORTS.READ} />}
        />
        <Route
          path="performance-trend"
          element={<PermissionGate showError children={<PerformanceTrendPage />} permission={PERMISSIONS.REPORTS.READ} />}
        />
        <Route
          path="product-insights"
          element={<PermissionGate showError children={<ProductInsightsPage />} permission={PERMISSIONS.REPORTS.READ} />}
        />
        <Route
          path="cashReport"
          element={<PermissionGate showError children={<CashReportPage />} permission={PERMISSIONS.REPORTS.READ} />}
        />
        <Route
          path="delivery-challenge-analytics"
          element={
            <PermissionGate showError children={<DeliveryChallengeAnalyticsPage />} permission={PERMISSIONS.DELIVERY_CHALLENGES.READ} />
          }
        />
        <Route path="app-usage" element={<PermissionGate showError children={<AppUsagePage />} permission={PERMISSIONS.REPORTS.READ} />} />
      </Route>
    </Route>
    <Route path="*" element={<MaintenanceError />} />
  </Route>
);

export default MainRoutes;
